import { clearAllWizardRequesterSessionCaches } from './wizardRequesterSessionCache';
import { clearSendInquiryResumeStorage } from './sendInquiryResume';

const STORAGE_KEY = 'nky_wizard_client_session_id';

/** Persists the active wizard search request document id for provider/storefront flows. */
export const WIZARD_SEARCH_REQUEST_ID_STORAGE_KEY =
  'nky_wizard_active_search_request_id';

const WIZARD_SEARCH_FP_META_KEY = `${WIZARD_SEARCH_REQUEST_ID_STORAGE_KEY}_meta`;

/**
 * Stable fingerprint for the current wizard payload so a new problem (or a different note /
 * street address from the contact sheet) does not reuse an old request id.
 */
export function fingerprintWizardState(wizardState) {
  if (!wizardState) return '';
  const rs = wizardState.requestSummary || {};
  const fd = wizardState.formData || {};
  return [
    wizardState.serviceType || '',
    String(wizardState.subServiceType ?? ''),
    String(rs.problemSummary ?? ''),
    String(rs.urgency ?? ''),
    String(rs.location ?? ''),
    String(fd.details ?? '').trim(),
    String(fd.additionalDetails?.['Street address'] ?? '').trim(),
  ].join('::');
}

export function getPersistedWizardSearchRequestId(fp) {
  if (!fp) return null;
  try {
    const raw = sessionStorage.getItem(WIZARD_SEARCH_FP_META_KEY);
    const j = raw ? JSON.parse(raw) : null;
    if (j && j.fp === fp && j.id) return j.id;
  } catch {
    // ignore
  }
  return null;
}

export function setPersistedWizardSearchRequestId(id, fp) {
  if (!id || !fp) return;
  try {
    sessionStorage.setItem(WIZARD_SEARCH_REQUEST_ID_STORAGE_KEY, id);
    sessionStorage.setItem(
      WIZARD_SEARCH_FP_META_KEY,
      JSON.stringify({ id, fp }),
    );
  } catch {
    // ignore
  }
}

/** Last persisted id (same tab) when fingerprint cannot be derived (e.g. direct URL). */
export function getLastWizardSearchRequestId() {
  try {
    return sessionStorage.getItem(WIZARD_SEARCH_REQUEST_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getOrCreateWizardClientSessionId() {
  try {
    let id = sessionStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = newSessionId();
      sessionStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return newSessionId();
  }
}

export function getWizardClientSessionId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clears browser-tab session data used for service wizard search (guest session id,
 * active search request id / fingerprint, guest contact cache, send-inquiry resume).
 * Call on logout so the next visitor or signed-out session does not inherit wizard context.
 */
export function clearServiceWizardSessionStorage() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(WIZARD_SEARCH_REQUEST_ID_STORAGE_KEY);
    sessionStorage.removeItem(WIZARD_SEARCH_FP_META_KEY);
  } catch {
    // ignore
  }
  clearAllWizardRequesterSessionCaches();
  clearSendInquiryResumeStorage();
}
